import { inject, Injectable } from '@angular/core'
import { AuthService } from './auth.service'

@Injectable({
  providedIn: 'root',
})
export class HttpService {
  private authService = inject(AuthService)
  async get<T>(url: string, abortSignal: AbortSignal): Promise<T> {
    const token = await this.authService.getAccessToken()
    const headers = new Headers()
    headers.append('Authorization', `Bearer ${token}`)

    // Using /api to work with proxy config
    return fetch(`/api${url}`, { signal: abortSignal, headers: headers }).then(
      async response => {
        if (response.ok) {
          return response.json()
        } else {
          return Promise.reject(await this.createRequestError(response))
        }
      }
    )
  }

  async downloadFile(url: string, abortSignal?: AbortSignal): Promise<void> {
    const token = await this.authService.getAccessToken()
    const headers = new Headers()
    headers.append('Authorization', `Bearer ${token}`)

    const response = await fetch(`/api${url}`, {
      signal: abortSignal,
      headers: headers,
    })

    if (!response.ok) {
      throw new Error(`Request failed with status ${response.status}`)
    }

    const blob = await response.blob()
    const filename = this.extractFilenameFromResponse(response)
    const downloadUrl = window.URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = downloadUrl
    if (filename) link.download = filename
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    window.URL.revokeObjectURL(downloadUrl)
  }

  private extractFilenameFromResponse(response: Response): string | null {
    const contentDisposition = response.headers.get('Content-Disposition')
    if (!contentDisposition) {
      return null
    }

    const filenameMatch = contentDisposition.match(
      /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/
    )
    if (filenameMatch && filenameMatch[1]) {
      let filename = filenameMatch[1]
      filename = filename.replace(/^["']|["']$/g, '')

      if (filename.startsWith("UTF-8''")) {
        filename = decodeURIComponent(filename.substring(7))
      }
      return filename
    }
    return null
  }

  async post<T>(url: string, body: any, abortSignal: AbortSignal): Promise<T> {
    const token = await this.authService.getAccessToken()
    const headers = new Headers()
    headers.append('Authorization', `Bearer ${token}`)
    headers.append('Content-Type', 'application/json')

    return fetch(`/api${url}`, {
      method: 'POST',
      headers: headers,
      body: JSON.stringify(body),
      signal: abortSignal,
    }).then(async response => {
      if (!response.ok) {
        return Promise.reject(await this.createRequestError(response))
      }
      // Check if response has content by trying to read it
      const text = await response.text()
      return text ? JSON.parse(text) : null
    })
  }

  async put<T>(url: string, body: any, abortSignal: AbortSignal): Promise<T> {
    const token = await this.authService.getAccessToken()
    const headers = new Headers()
    headers.append('Authorization', `Bearer ${token}`)
    headers.append('Content-Type', 'application/json')

    return fetch(`/api${url}`, {
      method: 'PUT',
      headers: headers,
      body: JSON.stringify(body),
      signal: abortSignal,
    }).then(async response => {
      if (!response.ok) {
        return Promise.reject(await this.createRequestError(response))
      }

      const text = await response.text()
      return text ? JSON.parse(text) : null
    })
  }

  async delete<T>(url: string, abortSignal: AbortSignal): Promise<T> {
    const token = await this.authService.getAccessToken()
    const headers = new Headers()
    headers.append('Authorization', `Bearer ${token}`)

    return fetch(`/api${url}`, {
      method: 'DELETE',
      headers: headers,
      signal: abortSignal,
    }).then(async response => {
      if (!response.ok) {
        return Promise.reject(await this.createRequestError(response))
      }

      const text = await response.text()
      return text ? JSON.parse(text) : null
    })
  }

  private async createRequestError(response: Response) {
    const fallbackMessage = `Request failed with status ${response.status}`
    const text = await response.text().catch(() => '')
    if (!text) return new Error(fallbackMessage)

    try {
      const errorData = JSON.parse(text) as unknown
      if (typeof errorData === 'string') return new Error(errorData)
      if (this.isErrorResponse(errorData)) {
        return new Error(
          errorData.detail ?? errorData.message ?? fallbackMessage
        )
      }
    } catch {
      return new Error(text)
    }

    return new Error(fallbackMessage)
  }

  private isErrorResponse(
    value: unknown
  ): value is { detail?: string; message?: string } {
    return typeof value === 'object' && value !== null
  }
}
